import { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  useTheme,
  Paper,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
  Divider,
  Popper,
  Fade,
  Tooltip,
  useMediaQuery,
  IconButton,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import HomeIcon from '@material-ui/icons/Home';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { SidebarItem } from '@backstage/core-components';
import {
  CATALOG_SIDEBAR_PATH,
  normalizePath,
  DEFAULT_LANDING_PAGE,
} from './sidebarConstants';
import { TOP_NAV_HEIGHT } from '../TopNav';
import { useSidebarPreferences } from './SidebarPreferencesContext';
import { useFlyout } from './FlyoutContext';
import { MOBILE_MEDIA_QUERY } from './mobileBreakpoint';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  sidebarItem: {
    width: '100%',
  },
  flyoutPopper: {
    zIndex: 1300,
  },
  flyoutPaper: {
    minWidth: 220,
    maxWidth: 280,
    width: 'calc(100vw - 32px)',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    overflow: 'hidden',
  },
  flyoutHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  flyoutTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  flyoutMenuItem: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuIcon: {
    minWidth: 36,
  },
  landingIcon: {
    color: theme.palette.primary.main,
  },
  // Sister icon: soft white, distinct from full-opacity nav icons; avoids Bayer
  // green which is reserved for selection indicators.
  landingPageMarker: {
    color: theme.palette.common.white,
    opacity: 0.75,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileLandingButton: {
    color: theme.palette.navigation.color,
    padding: 6,
  },
}));

interface CustomSidebarItemProps {
  icon: React.ComponentType<any>;
  to: string;
  text: string;
  className?: string;
  /** If true, disable the "Hide from Sidebar" option (for essential items like Settings) */
  cannotBeHidden?: boolean;
}

export const CustomSidebarItem = ({
  icon: Icon,
  to,
  text,
  className,
  cannotBeHidden = false,
}: CustomSidebarItemProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { landingPage, hiddenItems, setLandingPage, hideItem } =
    useSidebarPreferences();
  const {
    activeItemId,
    anchorEl,
    openFlyout,
    closeFlyout,
    scheduleClose,
    cancelClose,
  } = useFlyout();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const normalizedTo = normalizePath(to);
  // Compared on the path alone: a landing page may carry a query string (the
  // catalog deep links the retired /docs and /api-docs entries migrate to),
  // and it should still mark the item that owns that path.
  const isLandingPage =
    landingPage?.split(/[?#]/)[0] === normalizedTo ||
    (!landingPage && normalizedTo === DEFAULT_LANDING_PAGE);
  const isHidden = hiddenItems.includes(normalizedTo);
  const isOpen = activeItemId === normalizedTo;

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      openFlyout(normalizedTo, event.currentTarget);
    },
    [normalizedTo, openFlyout],
  );

  const handleMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const handleSetLandingPage = useCallback(async () => {
    try {
      const wasLandingPage = isLandingPage;
      await setLandingPage(isLandingPage ? undefined : normalizedTo);
      setSnackbarMessage(
        wasLandingPage
          ? `"${text}" removed as landing page`
          : `"${text}" set as landing page`,
      );
      setSnackbarOpen(true);
      closeFlyout();
    } catch (error) {
      // Error handling is done in the context
    }
  }, [isLandingPage, normalizedTo, setLandingPage, closeFlyout, text]);

  const handleHideFromSidebar = useCallback(async () => {
    try {
      await hideItem(normalizedTo);
      closeFlyout();
    } catch (error) {
      // Error handling is done in the context
    }
  }, [normalizedTo, hideItem, closeFlyout]);

  const handleMobileLandingPage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void handleSetLandingPage();
    },
    [handleSetLandingPage],
  );

  // EntityListProvider writes filters with history.replaceState, so React
  // Router can still see a bare /catalog while the browser URL has filters.
  // Re-clicking Catalog would then be a NavLink no-op. Force a bare landing
  // (new location.key) so CustomCatalogPage can re-apply preferred / remount.
  const handleCatalogHomeClick = useCallback(
    (event: React.MouseEvent) => {
      if (
        normalizedTo !== CATALOG_SIDEBAR_PATH ||
        location.pathname !== CATALOG_SIDEBAR_PATH
      ) {
        return;
      }
      // Let Ctrl/Cmd/Shift/middle-click open Catalog in a new tab/window.
      // Only an ordinary primary click forces the same-tab landing reset.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      window.history.replaceState(
        window.history.state,
        document.title,
        CATALOG_SIDEBAR_PATH,
      );
      navigate(
        { pathname: CATALOG_SIDEBAR_PATH, search: '' },
        {
          replace: true,
          state: { catalogLandingKey: `${Date.now()}` },
        },
      );
    },
    [normalizedTo, location.pathname, navigate],
  );

  // Hide this item if it's in the hidden items list
  if (isHidden) {
    return null;
  }

  return (
    <div
      className={classes.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarItem
        icon={Icon}
        to={normalizedTo}
        text={text}
        className={`${classes.sidebarItem} ${className || ''}`}
        onClick={
          normalizedTo === CATALOG_SIDEBAR_PATH
            ? handleCatalogHomeClick
            : undefined
        }
      >
        {isMobile ? (
          <Tooltip
            title={isLandingPage ? 'Your landing page' : 'Set as landing page'}
            arrow
            placement="left"
          >
            <span>
              <IconButton
                className={classes.mobileLandingButton}
                aria-label={
                  isLandingPage ? 'Your landing page' : 'Set as landing page'
                }
                disabled={isLandingPage}
                onClick={handleMobileLandingPage}
                size="small"
              >
                <HomeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : isLandingPage ? (
          <Tooltip title="Your landing page" arrow placement="right">
            <span
              className={classes.landingPageMarker}
              aria-label={`${text} is your landing page`}
            >
              <HomeIcon fontSize="small" />
            </span>
          </Tooltip>
        ) : null}
      </SidebarItem>
      <Popper
        open={isOpen && !isMobile}
        anchorEl={anchorEl}
        placement={isMobile ? 'bottom-start' : 'right-start'}
        transition
        className={classes.flyoutPopper}
        modifiers={{
          offset: {
            enabled: true,
            offset: '0, 4',
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper
              className={classes.flyoutPaper}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className={classes.flyoutHeader}>
                <Typography className={classes.flyoutTitle}>{text}</Typography>
              </div>
              <Divider />
              <MenuItem
                className={classes.flyoutMenuItem}
                onClick={isLandingPage ? undefined : handleSetLandingPage}
                disabled={isLandingPage}
              >
                <ListItemIcon className={classes.menuIcon}>
                  <HomeIcon
                    className={isLandingPage ? classes.landingIcon : undefined}
                    fontSize="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Set as Landing Page" />
              </MenuItem>
              {!cannotBeHidden && !isLandingPage && (
                <MenuItem
                  className={classes.flyoutMenuItem}
                  onClick={handleHideFromSidebar}
                >
                  <ListItemIcon className={classes.menuIcon}>
                    <VisibilityOffIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Hide from Sidebar" />
                </MenuItem>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ zIndex: theme.zIndex.snackbar, top: TOP_NAV_HEIGHT + 8 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

// Hook to get the landing page URL
export const useLandingPage = () => {
  const { landingPage } = useSidebarPreferences();
  return landingPage || '/';
};

// Hook to get and manage hidden sidebar items
export const useHiddenSidebarItems = () => {
  const { hiddenItems, unhideItem, unhideAll } = useSidebarPreferences();
  return { hiddenItems, unhideItem, unhideAll };
};
